import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Shield, ShieldCheck, Trash2, Loader2, UserCheck, UserX, Sprout, Landmark, PanelLeft, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { WorkflowAssignmentsCard } from '../components/WorkflowAssignmentsCard';
import { ClaudeConnectorCard } from '../components/admin/ClaudeConnectorCard';
import { SecuritySection } from '../components/SecuritySection';
import { ConnectedMailboxesCard } from '../components/ConnectedMailboxesCard';
import { WebexCard } from '../components/WebexCard';
import { MenuVisibilityDialog } from '../components/MenuVisibilityDialog';

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [menuMember, setMenuMember] = useState(null);

  const loadTeam = () => {
    api.listTeam()
      .then(res => setMembers(res.data))
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load team'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTeam(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      toast.success(`Role updated to ${newRole}`);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleTdAccess = async (userId, enabled) => {
    try {
      await api.setTeamDevAccess(userId, enabled);
      toast.success(enabled ? 'Team Development freigeschaltet 🌱' : 'Team Development Zugriff entfernt');
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access');
    }
  };

  const handleIautoAccess = async (userId, enabled) => {
    try {
      await api.setIautoAccess(userId, enabled);
      toast.success(enabled ? 'iAuto Workspace freigeschaltet — erscheint im Outreach-Menü' : 'iAuto Workspace Zugriff entfernt');
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access');
    }
  };

  const handleInfraAccess = async (userId, level) => {
    try {
      const r = await api.setInfraAccess(userId, level);
      toast.success(r.data.message);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access');
    }
  };

  const handleRemove = async (userId) => {
    try {
      await api.removeUser(userId);
      toast.success('User removed');
      setConfirmDelete(null);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.approveUser(userId);
      toast.success('User approved — they can now sign in');
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.rejectUser(userId);
      toast.success('Request declined');
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  const admins = members.filter(m => m.role === 'admin');
  const analysts = members.filter(m => m.role === 'analyst');
  const acMembers = members.filter(m => m.role === 'ac_member');
  const pendingRequests = members.filter(m => m.status === 'pending');

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="team-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">Team & Roles</h1>
        <p className="text-muted-foreground text-sm mt-1">{members.length} member{members.length !== 1 ? 's' : ''} in your organization — access, roles and workflow responsibilities</p>
      </div>

      {/* Pending Approval Requests */}
      {pendingRequests.length > 0 && (
        <div className="border border-amber-500/40 rounded-sm bg-amber-500/5 p-4" data-testid="pending-requests">
          <h2 className="text-sm font-medium text-amber-400 flex items-center gap-2 mb-3">
            <UserX className="w-4 h-4" /> Pending Access Requests ({pendingRequests.length})
          </h2>
          <p className="text-xs text-muted-foreground mb-3">New sign-ups can't access anything until you approve them.</p>
          <div className="space-y-2">
            {pendingRequests.map((m, i) => (
              <div key={m.id} className="flex items-center justify-between gap-3 bg-card border border-border rounded-sm px-3 py-2" data-testid={`pending-request-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                  <p className="text-[11px] text-muted-foreground font-mono truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" onClick={() => handleApprove(m.id)} data-testid={`approve-user-${i}`}
                    className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm">
                    <UserCheck className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleReject(m.id)} data-testid={`reject-user-${i}`}
                    className="h-8 px-3 text-xs text-red-400 border border-red-500/30 hover:bg-red-950/30 rounded-sm">
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="border border-border rounded-sm bg-card p-4" data-testid="team-stat-total">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Members</p>
          <p className="font-mono text-2xl font-bold text-foreground mt-1">{members.length}</p>
        </div>
        <div className="border border-border rounded-sm bg-card p-4" data-testid="team-stat-admins">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Admins</p>
          <p className="font-mono text-2xl font-bold text-blue-400 mt-1">{admins.length}</p>
        </div>
        <div className="border border-border rounded-sm bg-card p-4" data-testid="team-stat-analysts">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">OLTA Team</p>
          <p className="font-mono text-2xl font-bold text-emerald-400 mt-1">{analysts.length}</p>
        </div>
        <div className="border border-border rounded-sm bg-card p-4" data-testid="team-stat-ac-members">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">AC Members</p>
          <p className="font-mono text-2xl font-bold text-purple-400 mt-1">{acMembers.length}</p>
        </div>
      </div>

      {/* Members Table */}
      <div className="border border-border rounded-sm overflow-hidden" data-testid="team-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground font-bold">
              <th className="text-left py-2.5 px-4">Member</th>
              <th className="text-left py-2.5 px-4">Role</th>
              <th className="text-left py-2.5 px-4">2FA Status</th>
              <th className="text-left py-2.5 px-4">Joined</th>
              <th className="text-right py-2.5 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m, i) => {
              const isCurrentUser = m.id === user?.id;
              return (
                <tr key={m.id} data-testid={`team-member-${i}`}
                  className="border-t border-border hover:bg-secondary/30 transition-colors duration-100">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-mono"
                        style={(() => {
                          const r = m.role;
                          const bg = r === 'admin' ? 'rgba(59,130,246,0.2)' : r === 'ac_member' ? 'rgba(168,85,247,0.2)' : 'rgba(16,185,129,0.2)';
                          const fg = r === 'admin' ? '#93c5fd' : r === 'ac_member' ? '#c4b5fd' : '#6ee7b7';
                          return { background: bg, color: fg };
                        })()}>
                        {m.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {m.name} {isCurrentUser && <span className="text-xs text-muted-foreground ml-1">(you)</span>}
                          {m.status === 'pending' && <span className="ml-2 text-[9px] uppercase font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30">Pending</span>}
                          {m.status === 'rejected' && <span className="ml-2 text-[9px] uppercase font-mono px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-400 border border-red-500/30">Revoked</span>}
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono">{m.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {isCurrentUser ? (
                      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-sm border"
                        style={{ background: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.3)', color: '#93c5fd' }}>
                        <ShieldCheck className="w-3 h-3" /> Admin
                      </span>
                    ) : (
                      <select
                        data-testid={`role-select-${i}`}
                        value={m.role}
                        onChange={(e) => handleRoleChange(m.id, e.target.value)}
                        className="text-xs bg-secondary border border-border text-foreground rounded-sm px-2 py-1 cursor-pointer focus:border-blue-500 focus:outline-none"
                      >
                        <option value="admin">Admin</option>
                        <option value="analyst">OLTA Team</option>
                        <option value="ac_member">AC Member</option>
                      </select>
                    )}
                    {m.role === 'ac_member' && (
                      <button onClick={() => handleTdAccess(m.id, !m.team_dev_access)} data-testid={`td-access-toggle-${i}`}
                        title="Zugriff auf das Team-Development-Modul (Feedback, Ziele, Monats-Runde)"
                        className={`mt-1.5 flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border transition-colors ${
                          m.team_dev_access
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                            : 'border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}>
                        <Sprout className="w-3 h-3" /> Team Dev: {m.team_dev_access ? 'An' : 'Aus'}
                      </button>
                    )}
                    {m.role === 'analyst' && (
                      <button onClick={() => handleIautoAccess(m.id, !m.iauto_access)} data-testid={`iauto-access-toggle-${i}`}
                        title="iAuto Workspace (Druck, Umschläge, Versand) im Outreach-Menü freischalten — Admins sehen ihn immer"
                        className={`mt-1.5 flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border transition-colors ${
                          m.iauto_access
                            ? 'border-purple-500/40 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20'
                            : 'border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}>
                        <Printer className="w-3 h-3" /> iAuto: {m.iauto_access ? 'An' : 'Aus'}
                      </button>
                    )}
                    {m.role !== 'ac_member' && (
                      <div className="mt-1.5 flex items-center gap-1.5" data-testid={`infra-access-row-${i}`}
                        title="Infra-Fonds-Datenbank: „nur Deals suchen“ = bestehende Fonds/Themen screenen (nur Claude-Scoring) · „voll“ = Fonds anlegen, Perplexity-Research, Deals importieren/recherchieren (kostet)">
                        <Landmark className={`w-3 h-3 ${m.infra_access === 'none' ? 'text-muted-foreground/50' : 'text-amber-400'}`} />
                        {m.infra_access_fixed ? (
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300" data-testid={`infra-access-fixed-${i}`}>
                            Infra-Fonds: voll (fest)
                          </span>
                        ) : (
                          <select value={m.infra_access || 'none'} onChange={(e) => handleInfraAccess(m.id, e.target.value)} data-testid={`infra-access-select-${i}`}
                            className={`text-[10px] font-mono rounded-full border px-2 py-0.5 cursor-pointer focus:outline-none bg-secondary ${
                              m.infra_access === 'full' ? 'border-amber-500/40 text-amber-300' : m.infra_access === 'screen' ? 'border-sky-500/40 text-sky-300' : 'border-border text-muted-foreground'}`}>
                            <option value="none">Infra-Fonds: kein Zugriff</option>
                            <option value="screen">Infra-Fonds: nur Deals suchen</option>
                            <option value="full">Infra-Fonds: voll (kostet)</option>
                          </select>
                        )}
                      </div>
                    )}
                    {m.role !== 'admin' ? (
                      <button onClick={() => setMenuMember(m)} data-testid={`menu-config-btn-${i}`}
                        title="Welche Menüpunkte dieses Mitglied in der Seitenleiste sieht (nur Optik — Rechte bleiben)"
                        className={`mt-1.5 flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border transition-colors ${
                          (m.hidden_menu_items || []).length > 0
                            ? 'border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20'
                            : 'border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}>
                        <PanelLeft className="w-3 h-3" /> Menü: {(m.hidden_menu_items || []).length > 0 ? `${m.hidden_menu_items.length} ausgeblendet` : 'alles'}
                      </button>
                    ) : (
                      <span className="mt-1.5 flex items-center gap-1 text-[10px] font-mono text-muted-foreground/60" data-testid={`menu-config-admin-${i}`}>
                        <PanelLeft className="w-3 h-3" /> Menü: alles (Admin)
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {m.totp_verified ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                        <UserCheck className="w-3.5 h-3.5" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                        <UserX className="w-3.5 h-3.5" /> Pending
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-muted-foreground font-mono">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {!isCurrentUser && (
                      confirmDelete === m.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-red-400">Remove?</span>
                          <Button variant="ghost" size="sm" data-testid={`confirm-remove-${i}`}
                            onClick={() => handleRemove(m.id)}
                            className="h-7 px-2 text-xs text-red-400 hover:bg-red-950/30">
                            Yes
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={() => setConfirmDelete(null)}
                            className="h-7 px-2 text-xs text-muted-foreground">
                            No
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" data-testid={`remove-user-${i}`}
                          onClick={() => setConfirmDelete(m.id)}
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400 hover:bg-red-950/30">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Workflow role assignments */}
      <WorkflowAssignmentsCard />

      {/* Claude Connector (remote MCP) */}
      <ClaudeConnectorCard />

      <MenuVisibilityDialog member={menuMember} open={!!menuMember} onOpenChange={(o) => { if (!o) setMenuMember(null); }} onSaved={loadTeam} />

      {/* Connected Microsoft mailboxes health */}
      <ConnectedMailboxesCard />

      <WebexCard />

      {/* Security monitor: rate limiting & anomaly detection */}
      <SecuritySection />

      {/* Permissions Legend */}
      <div className="border border-border rounded-sm bg-card p-4" data-testid="permissions-legend">
        <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-400" /> Role Permissions
        </h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs">
          <div className="text-muted-foreground" />
          <div className="flex gap-4">
            <span className="text-blue-400 w-16 font-medium">Admin</span>
            <span className="text-emerald-400 w-20 font-medium">OLTA Team</span>
            <span className="text-purple-400 font-medium">AC Member</span>
          </div>
          <div className="text-muted-foreground">View all deals</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-emerald-400 w-20">Yes</span>
            <span className="text-purple-400">From Under OLTA Review</span>
          </div>
          <div className="text-muted-foreground">Upload & analyze deals</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-emerald-400 w-20">Yes</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground">Add notes & cost items</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-emerald-400 w-20">Yes</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground">OLTA Team Approval</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-muted-foreground/40 w-20">—</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground">Batch upload</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-muted-foreground/40 w-20">—</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground">Delete projects</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-muted-foreground/40 w-20">—</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground">Budget & reimbursements</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-muted-foreground/40 w-20">—</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground">Manage team</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">Yes</span>
            <span className="text-muted-foreground/40 w-20">—</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground" data-testid="legend-infra-funds">Infra-Fonds-Datenbank</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">per user</span>
            <span className="text-emerald-400 w-20">per user</span>
            <span className="text-muted-foreground/40 w-20">—</span>
          </div>
          <div className="text-muted-foreground" data-testid="legend-menu">Sichtbare Menüpunkte</div>
          <div className="flex gap-4">
            <span className="text-blue-400 w-16">alle</span>
            <span className="text-emerald-400 w-20">per user</span>
            <span className="text-purple-400">per user</span>
          </div>
          <div className="col-span-2 text-[11px] text-muted-foreground/80 border-t border-border/40 pt-2">
            Infra-Fonds-Stufen (oben pro Mitglied): <span className="text-sky-300">nur Deals suchen</span> = Fonds-Datenbank ansehen und bestehende Fonds, Themen, Angles und Portfolio-Deals gegen unsere Datenbank screenen (nur Claude-Scoring) ·
            <span className="text-amber-300"> voll</span> = zusätzlich Fonds anlegen, Profil-Research (Perplexity), Deals importieren/recherchieren, löschen, pinnen — die kostenpflichtigen Aktionen.
          </div>
        </div>
      </div>
    </div>
  );
}
